import type { WikiNewProfession } from '../types/wiki.types';

export interface FlattenedProfession {
  profession: WikiNewProfession;
  depth: number;
  hasChildren: boolean;
}

// Color palette for profession hierarchy groups
// Uses muted tones that fit the dark theme with red accent
export const HIERARCHY_COLORS = [
  '#ef4444',                    // Red (accent) - primary groups
  'rgba(255, 255, 255, 0.7)',   // White/gray - secondary groups
  '#f59e0b',                    // Amber - third tier
  '#22c55e',                    // Green - fourth tier
  'rgba(255, 255, 255, 0.5)',   // Muted white - fifth tier
  '#dc2626',                    // Dark red - sixth tier
];

/**
 * Converts flat profession list into sorted list with tree structure info.
 * Children are placed immediately after their parent (depth-first order).
 */
export const flattenProfessionsWithDepth = (professions: WikiNewProfession[]): FlattenedProfession[] => {
  const result: FlattenedProfession[] = [];
  const map = new Map<number, WikiNewProfession>();
  const childrenMap = new Map<number, WikiNewProfession[]>();

  // Build maps
  professions.forEach(p => {
    map.set(p.id, p);
    const parentId = p.parentProfessionId ?? 0;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(p);
  });

  // Recursively add professions with their children
  const addWithChildren = (profession: WikiNewProfession, depth: number) => {
    const children = childrenMap.get(profession.id) || [];
    result.push({ profession, depth, hasChildren: children.length > 0 });
    children.forEach(child => addWithChildren(child, depth + 1));
  };

  // Start with root professions
  const roots = professions.filter(p =>
    !p.parentProfessionId || !map.has(p.parentProfessionId)
  );
  roots.forEach(root => addWithChildren(root, 0));

  return result;
};

export interface ProfessionHierarchyInfo {
  color: string;
  depth: number;
  hasChildren: boolean;
  isRoot: boolean;
}

/**
 * Builds hierarchy info map for professions.
 * Only assigns colors/indicators to professions that are part of a hierarchy
 * (root with children, or children). Standalone professions are excluded.
 */
export const buildProfessionHierarchyMap = (
  professions: WikiNewProfession[]
): Map<number, ProfessionHierarchyInfo> => {
  const flatProfessions = flattenProfessionsWithDepth(professions);
  const hierarchyMap = new Map<number, ProfessionHierarchyInfo>();
  const colorMap = new Map<number, string>();
  let colorIndex = 0;

  flatProfessions.forEach(({ profession, depth, hasChildren }) => {
    const isRoot = depth === 0;
    const isRootWithChildren = isRoot && hasChildren;

    let color: string;

    if (isRootWithChildren) {
      color = HIERARCHY_COLORS[colorIndex % HIERARCHY_COLORS.length];
      colorMap.set(profession.id, color);
      colorIndex++;
    } else if (profession.parentProfessionId && colorMap.has(profession.parentProfessionId)) {
      color = colorMap.get(profession.parentProfessionId)!;
      colorMap.set(profession.id, color);
    } else {
      // Standalone profession (root without children) - no hierarchy indicator
      return;
    }

    hierarchyMap.set(profession.id, { color, depth, hasChildren, isRoot });
  });

  return hierarchyMap;
};
